import {useConfirm} from '@primer/react'
import {useCallback} from 'react'

import type {Iteration} from '../api/columns/contracts/iteration'
import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import {GroupHeaderActionMenuUI, ItemBulkMoveIteration} from '../api/stats/contracts'
import type {MemexItemModel} from '../models/memex-item-model'
import {Resources} from '../strings'
import {usePostStats} from './common/use-post-stats'
import {useBulkUpdateItems} from './use-bulk-update-items'

export const useMoveItemsToIteration = () => {
  const {postStats} = usePostStats()
  const confirm = useConfirm()
  const {bulkUpdateSingleColumnValue} = useBulkUpdateItems()

  const moveItemsToIteration = useCallback(
    async (
      items: ReadonlyArray<MemexItemModel>,
      targetIteration: Iteration,
      columnId: number,
      isIssueField: boolean,
    ) => {
      if (items.length === 0) return

      const confirmed = await confirm({
        title: Resources.moveItemsToIterationConfirmationTitle(targetIteration.title),
        content: Resources.moveItemsToIterationConfirmationMessage(items.length, targetIteration.title),
        confirmButtonContent: 'Move',
      })

      if (!confirmed) return

      try {
        // Spread ReadonlyArray to mutable Array as required by bulkUpdateSingleColumnValue
        await bulkUpdateSingleColumnValue(
          [...items],
          {
            dataType: MemexColumnDataType.Iteration,
            memexProjectColumnId: columnId,
            isIssueField,
            value: {id: targetIteration.id},
          },
          `move items to ${targetIteration.title}`,
        )

        postStats({
          name: ItemBulkMoveIteration,
          ui: GroupHeaderActionMenuUI,
          numberOfRows: items.length,
          context: targetIteration.title,
        })
      } catch {
        // bulkUpdateSingleColumnValue already shows an error toast to the user.
        // We catch here to skip posting stats for failed moves.
      }
    },
    [confirm, bulkUpdateSingleColumnValue, postStats],
  )

  return {moveItemsToIteration}
}
